import React, { useRef, useEffect } from 'react';
import { Chart, ChartConfiguration, registerables } from 'chart.js';

Chart.register(...registerables);

type SimpleChartProps = {
  data: number[];
  colors?: [string, string]; // gradient colors
  height?: number;
  width?: number;
};

const LineChart: React.FC<SimpleChartProps> = ({
  data,
  colors = ['#2970FF', '#EFF4FF'],
  height = 80,
  width = 240,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    // Create vertical gradient
    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, colors[0]);
    gradient.addColorStop(1, colors[1]);

    // Chart config
    const config: ChartConfiguration<'line'> = {
      type: 'line',
      data: {
        labels: data.map((_, i) => i + 1),
        datasets: [
          {
            data,
            fill: true,
            backgroundColor: gradient,
            borderColor: colors[0], // <-- add border color here
            borderWidth: 3,         // <-- add border width here
            pointRadius: 0,
            tension: 0,
          },
        ],
      },
      options: {
        responsive: false,
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false },
        },
        scales: {
          x: { display: false, grid: { display: false } },
          y: { display: false, grid: { display: false } },
        },
        elements: {
          line: { borderWidth: 2 }, // <-- ensure borderWidth is set here too
        },
      },
    };

    // Destroy previous chart if exists
    if (chartRef.current) {
      chartRef.current.destroy();
    }

    chartRef.current = new Chart(ctx, config);

    // Cleanup
    return () => {
      chartRef.current?.destroy();
    };
  }, [data, colors, width, height]);

  return <canvas data-testid="chart" ref={canvasRef} width={width} height={height} />;
};

export default LineChart;